import React, { useEffect, useState } from "react";
import withLayout from "../../../hocs/Layout";
import { apiConstants, getDownload, postMultipart } from "../../../common/constants";
import { FormProvider, RHFFileField, RHFSelectField } from "../../../common/hook-form";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import { Box, Button, Grid, Typography, Tooltip, IconButton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { yupResolver } from "@hookform/resolvers/yup";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FileDownloadOutlined } from "@mui/icons-material";
import MUIModal from "../../../common/MUIModal";
import PageTitle from "../../../common/PageTitle";
import BulkOrderResults from "./BulkOrderResult";
import { useSnackbar } from "notistack";
import { getMerchant } from "../../../helpers/ReduxHelpers";
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { NAVY, PRIMARY_BLUE, SUBTLE } from '../../../styles/foundation/colors';
import {
  pageContainerSx,
  uploadSectionSx, primaryBtnSx, secondaryBtnSx, helpTextSx, helpTextTypeSx,
} from '../../../styles/patterns';

const BulkOrderCreate = (props) => {
  const shipmentType = props.shipmentType;
  const { enqueueSnackbar } = useSnackbar();
  const [bulkOrderIdentifier, setBulkOrderIdentifier] = useState()
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [sampleName, setSampleName] = useState()
  const merchant = getMerchant();
  const merchantId = merchant.id ? merchant.id : ''
  const [reload, setReload] = useState(0)
  const [open, setOpen] = useState(false)

  const methodsBulkUpload = useForm({
    resolver: yupResolver(Yup.object().shape({ orderFile: Yup.mixed().required("Please Select a File") })),
    defaultValues: { orderFile: '' }
  });
  const { handleSubmit: handleSubmitBulkUpload, reset: resetForm } = methodsBulkUpload;
  const list = ["Forward", "Reverse"]

  const createB2COrder = async (data) => {
    setIsUploadingFile(true)
    let form = new FormData();
    form.append("file", data.orderFile[0])
    form.append("bulkOrderIdentifier", bulkOrderIdentifier)
    await postMultipart(`${apiConstants.BULK_CREATE_ORDER_v2}?merchantOid=${merchantId}`, form).then((res) => {
      setReload(reload + 1);
      if (res.status === 200) enqueueSnackbar("Successfully Uploaded!", { variant: "success" });
      else enqueueSnackbar(`${res.data}`, { variant: 'error' })
    }).catch((err) => {
      console.log(err)
      enqueueSnackbar(err.response.data.message || "Error creating order", { variant: 'error' })
    }).finally(() => setIsUploadingFile(false))
  }

  const createB2BOrder = async (data) => {
    setIsUploadingFile(true)
    let form = new FormData();
    form.append("file", data.orderFile[0])
    await postMultipart(`${apiConstants.BULK_CREATE_ORDER_B2B}?merchantOId=${merchantId}`, form).then((res) => {
      setReload(reload + 1);
      if (res.status === 200) enqueueSnackbar("Successfully Uploaded!", { variant: "success" });
      else enqueueSnackbar(`${res.data}`, { variant: 'error' })
    }).catch((err) => {
      enqueueSnackbar(err.response.data.message || "Error creating order", { variant: 'error' })
    }).finally(() => setIsUploadingFile(false))
  }

  const onSubmitBulkUpload = async (data) => {
    try {
      if (shipmentType === 'B2B') createB2BOrder(data);
      else createB2COrder(data);
      setBulkOrderIdentifier("")
      resetForm()
    } catch (err) {
      const message = err?.response?.data?.message || err.message;
      enqueueSnackbar(message || "Error creating order", { type: "error" });
    }
    resetForm({ orderFile: '' })
    setBulkOrderIdentifier('')
  };

  useEffect(() => {
    if (bulkOrderIdentifier === "Forward") setSampleName("FORWARD_BULK_ORDER")
    if (bulkOrderIdentifier === "Reverse") setSampleName("REVERSE_BULK_ORDER")
    if (shipmentType === "B2B") setSampleName("B2B_BULK_ORDER_V1")
  }, [bulkOrderIdentifier, shipmentType])

  const handleBulkSample = () => {
    getDownload(`${apiConstants.BASE_LOCAL}${apiConstants.DOWNLOAD_SAMPLE_LOCATION}?fileName=${sampleName}`)
      .then((response) => {
        const binaryData = [response.data];
        const href = URL.createObjectURL(new Blob(binaryData, { type: "application/octet-stream" }));
        const link = document.createElement("a");
        link.href = href; link.setAttribute("download", "bulk-upload-sample.csv");
        document.body.appendChild(link); link.click(); document.body.removeChild(link);
        URL.revokeObjectURL(href);
      }).catch((err) => console.error(err));
  };

  return (
    <Box sx={props.embedded ? undefined : pageContainerSx}>
      <PageTitle subTitle="Upload a CSV file to create multiple shipments at once — forward or reverse">Create Bulk Shipments</PageTitle>
      {/* Upload section */}
      <Box sx={uploadSectionSx}>
        <Typography sx={{ fontFamily: FONT, fontSize: SIZE.md, fontWeight: WEIGHT.bold, color: NAVY, mb: 1.5 }}>
          Upload Shipment File
        </Typography>

        <Box sx={helpTextSx}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: PRIMARY_BLUE, mt: 0.2 }} />
          <Typography sx={helpTextTypeSx}>
            <strong>Instructions:</strong> Select the order type (Forward or Reverse), download the sample CSV,
            fill in the required columns, and upload. Orders will be processed in the background.
          </Typography>
        </Box>

        <FormProvider methods={methodsBulkUpload} onSubmit={handleSubmitBulkUpload(onSubmitBulkUpload)}>
          <Grid container spacing={2} alignItems="center">
            {shipmentType === 'B2C' && (
              <Grid item xs={12} sm={6} md={3}>
                <RHFSelectField name='bulkOrderIdentifier' label='Select Order Type *'
                  options={list.map((val) => ({ value: val, label: val }))}
                  value={bulkOrderIdentifier} width='100%' onChange={(e) => setBulkOrderIdentifier(e.target.value)} required />
              </Grid>
            )}
            <Grid item xs={12}>
              <RHFFileField name="orderFile" accept={["csv"]} required={true} />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <LoadingButton startIcon={<FileUploadIcon />} type="submit" loading={isUploadingFile}
                  variant="contained" size="small" sx={primaryBtnSx}>
                  Upload
                </LoadingButton>
                {sampleName ? (
                  <Button variant="outlined" size="small" sx={secondaryBtnSx}
                    startIcon={<FileDownloadOutlined />} onClick={handleBulkSample}>
                    Sample Download
                  </Button>
                ) : (
                  <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                    Select order type to download sample
                  </Typography>
                )}
                <Tooltip title='Details of Sample Columns'>
                  <IconButton size='small' onClick={() => setOpen(true)} sx={{ color: PRIMARY_BLUE }}>
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </FormProvider>

        <MUIModal open={open} title="Sample Column Details" handleClose={() => setOpen(false)}>
          <Box sx={{ p: 2 }}>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>SkipRule</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                Possible values: true / false. Set true to process via a specific courier; false to follow courier allocation rules.
              </Typography>
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Courier Name</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                Should be blank if "SkipRule" is "false".
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE.sm, fontWeight: WEIGHT.bold, color: NAVY }}>Is Reverse</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE }}>
                Should be "false" for Forward orders.
              </Typography>
            </Box>
          </Box>
        </MUIModal>
      </Box>

      {/* Upload history */}
      <BulkOrderResults shipmentType={shipmentType} reload={reload} />
    </Box>
  );
};
export default withLayout(BulkOrderCreate);
